// ./src/pages/private/EventMessageComposer/useGuestMessageSend.ts
//
// The SERVER half of sending guests a message — invitations or event updates — in ONE place, shared by the full
// composer (Communications › Invitations / Event updates) and the Create Event wizard's finish screen (ROADMAP A2b #62
// P6c, owner 2026-09-29: "the final step should offer preview, publish and send invitations right from there").
//
// It owns: the ready-made message catalog for the channel, the DRY RUN (who would get it, at what cost), one guest's
// RENDERED preview, and the SEND. The screens own everything the host edits (recipients, the message box, the channel)
// and pass the message in on each call — so a stale closure can never send yesterday's text.
//
// ⚠ Two screens, one pipeline: a fix to how a send is built (the WhatsApp template rule, the site origin, the format)
//   lands in both. Before this, the finish screen would have needed a second copy of all of it.
// ⚠ Every failure lands in `error` — a refused send is classified by the caller (`classifySendRefusal`), anything else
//   is an ErrorAlert. Nothing here toasts.
// ⓘ #62 P7 — a REAL send (never a dry run) is reported to analytics here, once for both screens: `invitation_send` /
//   `event_update_send` with the channel, how many went, and `from` — which every caller must say (it is required).

import { useCallback, useEffect, useRef, useState } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  EventGuestService,
  InvitationCatalog,
  InvitationCatalogMessage,
  InvitationPreview,
  InvitationSendResult,
  SendInvitationsInput,
} from "@/utils/api";
import { eventsWebOrigin } from "@/utils/eventPublicUrl";
import { analytics, GUEST_MESSAGE_EVENTS, guestMessageSendParams, SendSource } from "@/utils/analytics";

export type ComposerMode = "invitation" | "event_update";
// WhatsApp sends an APPROVED template (no host body).
export type MessageChannel = "email" | "sms" | "whatsapp";

const escapeHtml = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** A catalog body (plain text, blank-line paragraphs) as the email editor's HTML. */
export const plainToHtml = (text: string): string =>
  text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

export const htmlHasText = (html: string): boolean => html.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim().length > 0;

/** What a call sends — the message as the host has it NOW. */
export interface MessageContent {
  message: string | null;
  messageFormat: "html" | "plain";
  /** Only WhatsApp sends a chosen catalog template; email and SMS render a body. */
  templateId?: string | null;
}

const SERVICES: Record<
  ComposerMode,
  {
    fetchCatalog: (eventId: string, channel: MessageChannel) => Promise<OpResult>;
    send: (params: SendInvitationsInput) => Promise<OpResult>;
    previewMode?: string;
  }
> = {
  invitation: {
    fetchCatalog: (eventId, channel) => EventGuestService.invitationCatalog(eventId, channel),
    send: (params) => EventGuestService.sendInvitations(params),
    previewMode: undefined,
  },
  event_update: {
    fetchCatalog: (eventId, channel) => EventGuestService.eventUpdateCatalog(eventId, channel),
    send: (params) => EventGuestService.sendEventUpdate(params),
    previewMode: "event_update",
  },
};

export interface UseGuestMessageSendArgs {
  eventId: string | null | undefined;
  mode: ComposerMode;
  channel: MessageChannel;
  /** Which screen sends — for the funnel's `invitation_send`. ⚠ Required, so a new screen cannot go unattributed. */
  from: SendSource;
  /** Load the catalog for the channel (off for a screen that has none). */
  withCatalog?: boolean;
  /** Called each time a channel's catalog arrives — the screen decides what to pre-fill. */
  onCatalogLoaded?: (catalog: { messages: InvitationCatalogMessage[]; recommendedId: string | null; channel: MessageChannel }) => void;
}

export const useGuestMessageSend = ({ eventId, mode, channel, from, withCatalog = true, onCatalogLoaded }: UseGuestMessageSendArgs) => {
  const services = SERVICES[mode];

  const [catalog, setCatalog] = useState<InvitationCatalogMessage[]>([]);
  const [recommendedId, setRecommendedId] = useState<string | null>(null);
  // ⚠ The catalog read's own outcome: a screen that pre-fills from it must be able to say it could not.
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState<OpResult | null>(null);
  const [preview, setPreview] = useState<InvitationSendResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  /**
   * #62 P6c Phase B — how many of a family's OTHER adults the previewed send also reaches (their own copies). Null = not
   * known. ⓘ Its own dry run, fail-soft: a failure drops this one line, never the preview (see `previewLinkedAdults`).
   */
  const [linkedAdults, setLinkedAdults] = useState<number | null>(null);
  const [rendered, setRendered] = useState<InvitationPreview | null>(null);
  const [rendering, setRendering] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setErrorState] = useState<OpResult | null>(null);
  /**
   * Which step `error` came from. ⚠ A failed PREVIEW titled "Could not send" tells a host something went out that did
   * not; the screen names the step and offers the retry that fits it.
   */
  const [errorSource, setErrorSource] = useState<'preview' | 'render' | 'send' | 'other' | null>(null);
  // ⚠ A later dry run / render supersedes an earlier one — picking another message quickly must not let the slower,
  //   OLDER answer land last and describe a message the host is no longer sending.
  const previewCallRef = useRef(0);
  const renderCallRef = useRef(0);

  const failWith = (result: OpResult | null, source: 'preview' | 'render' | 'send' | 'other' | null) => {
    setErrorState(result);
    setErrorSource(result ? source : null);
  };

  /** For the screens' own reads (the recipient list) — the same surface, marked as not a send. */
  const setError = useCallback((result: OpResult | null) => {
    setErrorState(result);
    setErrorSource(result ? 'other' : null);
  }, []);

  // Read through a ref so a screen passing an inline callback does not re-fetch the catalog every render.
  const onLoadedRef = useRef(onCatalogLoaded);
  onLoadedRef.current = onCatalogLoaded;

  useEffect(() => {
    if (!eventId || !withCatalog) {
      return;
    }

    let alive = true;

    setCatalogLoaded(false);
    setCatalogError(null);

    services
      .fetchCatalog(eventId, channel)
      .then((result) => {
        if (!alive) {
          return;
        }

        setCatalogLoaded(true);

        if (result.code !== OP_RESULT_CODES.OK) {
          setCatalogError(result);

          return;
        }

        const data = (result.getDataFirst?.() ?? (result as any).data?.[0]) as InvitationCatalog | undefined;
        const messages = data?.messages ?? [];

        setCatalog(messages);
        setRecommendedId(data?.recommendedId ?? null);
        onLoadedRef.current?.({ messages, recommendedId: data?.recommendedId ?? null, channel });
      })
      .catch((e) => {
        // ⚠ A throw must SETTLE the read too — left unsettled, a screen waiting for the catalog waits for ever.
        if (alive) {
          setCatalogLoaded(true);
          setCatalogError(OpResult.fromException(e));
        }
      });

    return () => {
      alive = false;
    };
  }, [eventId, channel, withCatalog, services]);

  const params = useCallback(
    (ids: string[], content: MessageContent): SendInvitationsInput => ({
      eventId: eventId as string,
      eventGuestIds: ids,
      siteOrigin: eventsWebOrigin(),
      message: content.message,
      messageFormat: content.messageFormat,
      channel,
      templateId: channel === "whatsapp" ? content.templateId ?? undefined : undefined,
    }),
    [eventId, channel],
  );

  /** The dry run: who would get it, who would be skipped, what it costs. Nothing is sent. */
  const runPreview = useCallback(
    async (ids: string[], content: MessageContent): Promise<InvitationSendResult | null> => {
      if (!eventId || ids.length === 0) {
        return null;
      }

      const call = ++previewCallRef.current;

      setPreviewing(true);
      failWith(null, null);

      const result = await services.send({ ...params(ids, content), dryRun: true });
      const data = result.code === OP_RESULT_CODES.OK ? ((Array.isArray(result.data) ? result.data[0] : null) as InvitationSendResult | null) : null;

      if (call !== previewCallRef.current) {
        return data;
      }

      if (result.code === OP_RESULT_CODES.OK) {
        setPreview(data);
      } else {
        setPreview(null);
        failWith(result, 'preview');
      }

      setPreviewing(false);
      setLinkedAdults(null);

      if (result.code === OP_RESULT_CODES.OK) {
        const linked = await EventGuestService.previewLinkedAdults(params(ids, content), mode);
        const count = linked.code === OP_RESULT_CODES.OK ? (linked.getDataFirst?.() as InvitationSendResult | undefined)?.linkedAdults : null;

        // ⚠ Only the newest preview may say how many family members it reaches.
        if (call === previewCallRef.current) {
          setLinkedAdults(typeof count === "number" ? count : null);
        }
      }

      return data;
    },
    [eventId, services, params, mode],
  );

  /** One guest's message exactly as it will arrive (subject + html / text). */
  const fetchRendered = useCallback(
    async (guestId: string, content: MessageContent): Promise<void> => {
      if (!eventId || !guestId) {
        return;
      }

      const call = ++renderCallRef.current;

      setRendering(true);

      const result = await EventGuestService.previewInvitation({
        eventId,
        eventGuestId: guestId,
        siteOrigin: eventsWebOrigin(),
        message: content.message,
        messageFormat: content.messageFormat,
        mode: services.previewMode,
        channel,
      });

      if (call !== renderCallRef.current) {
        return;
      }

      if (result.code === OP_RESULT_CODES.OK) {
        setRendered((Array.isArray(result.data) ? result.data[0] : null) as InvitationPreview | null);
      } else {
        setRendered(null);
        failWith(result, 'render');
      }

      setRendering(false);
    },
    [eventId, channel, services],
  );

  /** The real send. Returns the result on success, null on a refusal (which is in `error`). */
  const send = useCallback(
    async (ids: string[], content: MessageContent): Promise<InvitationSendResult | null> => {
      if (!eventId || ids.length === 0) {
        return null;
      }

      setSending(true);
      failWith(null, null);

      const result = await services.send(params(ids, content));

      setSending(false);

      if (result.code !== OP_RESULT_CODES.OK) {
        failWith(result, 'send');

        return null;
      }

      const sent = ((Array.isArray(result.data) ? result.data[0] : null) as InvitationSendResult | null) ?? {};

      analytics.sendEvent(
        GUEST_MESSAGE_EVENTS[mode],
        guestMessageSendParams({ channel, sent: sent.sent ?? 0, failed: sent.failed ?? 0, from }),
      );

      return sent;
    },
    [eventId, services, params, mode, channel, from],
  );

  return {
    catalog,
    recommendedId,
    catalogLoaded,
    catalogError,
    preview,
    setPreview,
    previewing,
    linkedAdults,
    runPreview,
    rendered,
    setRendered,
    rendering,
    fetchRendered,
    sending,
    send,
    error,
    errorSource,
    setError,
  };
};
